import { useEffect } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  IconBell,
  IconBellFilled,
  IconSettings,
  IconSettingsFilled,
  IconShare2,
} from "@tabler/icons-react";
import { isDesktopLayout } from "../../../common/hooks/useIsDesktopView";
import {
  openSettings,
  useSettingsSheet,
} from "../../../domains/app/stores/settingsSheetStore";
import {
  useCurrentUser,
  type CurrentUser,
} from "../../../domains/user/hooks/useCurrentUser";
import { shareUrl } from "../../../domains/app/utils/shareUrl";
import { goBackInApp } from "../../../domains/app/utils/backNavigation";
import { useApiQuery } from "../../../services/api";
import { iosSetBackgroundColor, isIOSApp } from "../../../tools/ios";
import { displayToast } from "../AppToast";
import ContentHeader from "./ContentHeader";
import { Badge } from "../../ui/Badge";
import { useTranslation } from "react-i18next";

export default function ProfileHeader({
  user,
}: {
  user: CurrentUser | null;
  title?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { pathname } = useLocation();
  const userId = user?.id ?? null;
  const { currentUser } = useCurrentUser();
  const isMe = currentUser && user?.id === currentUser.id;
  const personals = useApiQuery(
    "/webapp/private/notification/personals",
    currentUser && navigator.onLine ? {} : false
  );
  const broadcasts = useApiQuery(
    "/webapp/private/notification/broadcasts",
    currentUser && navigator.onLine ? {} : false
  );

  useEffect(() => {
    initialize();
  }, []);

  async function initialize(): Promise<void> {
    try {
      window.scrollTo(0, 0);
      if (isIOSApp()) iosSetBackgroundColor("#FFFFFF");
    } catch (error) {
      console.error("Couldn't mount profile bar.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  function handleNavigateBack(): void {
    const searchParams = new URLSearchParams(history.location.search);

    if (searchParams.get("shared")) {
      history.replace("/discovery");
      return;
    }

    goBackInApp(history, "/discovery");
  }

  async function handleShareProfile(): Promise<void> {
    try {
      if (currentUser === null) return;

      const text = t("Here is a user profile you might be interested in.");
      const url = `${globalThis.location.origin}/profile/user/${userId}?shared=${currentUser.id}`;
      const outcome = await shareUrl(
        document.title,
        text,
        Promise.resolve(url)
      );
      if (outcome === "sent") displayToast(t("Profile shared"), "success");
    } catch (error) {
      console.error(error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  const unreadNotificationsCount =
    personals.state === "success" && broadcasts.state === "success"
      ? [...personals.body, ...broadcasts.body].filter((n) => !n.read).length
      : 0;

  const isOnSettings = pathname.startsWith("/settings");
  const isOnNotifications = pathname.startsWith("/profile/notifications");

  // The settings sheet replaces the /settings route on mobile/native; desktop
  // web keeps the page route.
  const useSettingsSheetEntry = !isDesktopLayout();
  const isSettingsSheetOpen = useSettingsSheet().open;

  return (
    <ContentHeader
      onBack={handleNavigateBack}
      actions={
        user !== null && (
          <>
            {isMe ? (
              <>
                {useSettingsSheetEntry ? (
                  <button
                    type="button"
                    onClick={openSettings}
                    aria-label={t("Settings")}
                    className="inline-flex"
                  >
                    {isSettingsSheetOpen ? (
                      <IconSettingsFilled className="size-6 cursor-pointer text-primary-black" />
                    ) : (
                      <IconSettings className="size-6 cursor-pointer text-primary-black" />
                    )}
                  </button>
                ) : (
                  <Link to="/settings" className="inline-flex">
                    {isOnSettings ? (
                      <IconSettingsFilled className="size-6 cursor-pointer text-primary-black" />
                    ) : (
                      <IconSettings className="size-6 cursor-pointer text-primary-black" />
                    )}
                  </Link>
                )}
                <Link to="/profile/notifications" className="inline-flex mr-1">
                  <Badge show={unreadNotificationsCount > 0}>
                    {isOnNotifications ? (
                      <IconBellFilled className="size-6 cursor-pointer text-primary-black" />
                    ) : (
                      <IconBell className="size-6 cursor-pointer text-primary-black" />
                    )}
                  </Badge>
                </Link>
              </>
            ) : (
              <IconShare2
                className="size-6 cursor-pointer text-primary-black"
                onClick={() => {
                  void handleShareProfile();
                }}
              />
            )}
          </>
        )
      }
    />
  );
}
